import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import PropTypes from "prop-types";
import SettingsListItem from "../SettingsListItem";
import SettingsListHeader from "../SettingsListHeader";
import styles from "./SettingsList.module.css";
import { useAccount } from "../../utils/AccountInfoContext";
import { useAddresses } from "../../AddressesPage/useAddressesPage";
import mobile from "mobileBridge";
import { useTranslation } from "react-i18next";
import environment from "../../environment";

import Modal from "../Modal";
import CloseButton from "assets/Close_Button.svg";
import addressIcon from "assets/Addresses_Icon.svg";

export function SettingsList({ items, issuesCount }) {
  const { t } = useTranslation();
  const history = useHistory();
  const { activeUser, canAccessMT, subEntityData, cachedIBOInfo, consentData } =
    useAccount();

  const { data: addressData } = useAddresses();

  const isMobile = mobile.isApp();

  const WEB_APP_COUNTRY = environment?.WEB_APP_COUNTRY;
  const isANZ = WEB_APP_COUNTRY === "AU";

  const getPath = (label) => {
    switch (label) {
      case "Account Issues":
        return "/account-issues";
      case "Addresses":
        return "/addresses";
      case "Communication":
        return "/communication";
      case "Credit Cards":
        return "/credit-cards";
      case "DreamStream":
        return "/dreamstream";
      case "Emergency Contacts":
        return "/emergency-contacts";
      case "HomeSite":
        return "/homesite";
      case "Language":
        return "/language";
      case "GST & HST Taxes":
        return "/";
      case "Management Team":
        return "/management-team";
      case "Membership":
        return "/membership";
      case "Personal":
        return "/personal";
      case "Platinum Settings":
      case "Emerald Settings":
      case "Diamond Settings":
        return "/platinum-settings";
      case "Topics":
        return "/topics";
      // case "Privacy":
      //   return "/privacy";
      case "WWG News":
        return "/wwg-news";
      case "App List Settings":
        return "/applist-settings";
      case "Notification Settings":
        return "/notification-settings";
      case "HomeSite":
        return "/homesite";
      default:
        return "/";
    }
  };

  const [addressWarnIsOpen, setAddressWarnIsOpen] = useState(false);

  const handleItemClick = (label) => {
    if (addressData?.length < 1 && label == "HomeSite") {
      setAddressWarnIsOpen(true);
      return;
    }
    const path = getPath(label);
    if (path !== "/") {
      history.push(path);
    }
  };

  const handleSettingsClick = (label) => {
    if (!addressData && label == "HomeSite") {
      setAddressWarnIsOpen(true);
      return;
    }
    if (label === "App List Settings") {
      mobile.postMessage("open-app-list-settings", false);
    } else if (label === "Notification Settings") {
      mobile.postMessage("open-notification-settings", false);
    }
  };
  // console.log(cachedIBOInfo?.showTopicsSection);
  const accountItems = items
    .filter(
      (item) =>
        item.label !== "App List Settings" &&
        item.label !== "Notification Settings" &&
        !(
          !consentData &&
          (item.label === "Privacy" || (item.label === "Membership" && !isANZ))
        ) &&
        !(item.label === "HomeSite" && isANZ)
    )
    .map((item) => {
      if (item.label === "Platinum Settings" && cachedIBOInfo) {
        const { PinLevel } = cachedIBOInfo;
        console.log("PinLevel:", cachedIBOInfo);
        if (PinLevel === 4) {
          return { ...item, label: "Diamond Settings" };
        } else if (PinLevel === 3) {
          return { ...item, label: "Emerald Settings" };
        } else if (PinLevel === 2) {
          return { ...item, label: "Platinum Settings" };
        } else {
          return null; // Exclude Platinum Settings if the user is not at least Platinum
        }
      }
      return item;
    })
    .filter((item) => item !== null)
    .filter((item) => {
      if (
        isANZ &&
        [
          "Language",
          "Diamond Settings",
          "Emerald Settings",
          "Platinum Settings",
        ].includes(item.label)
      ) {
        return false;
      }
      if (item.label === "WWG News" && cachedIBOInfo?.PinLevel === 4) {
        return true;
      }
      if (item.label === "Topics") {
        return cachedIBOInfo?.showTopicsSection && !isMobile;
      }
      return item.label !== "WWG News";
    });

  //Temporary hidden
  // Add "Topics" only if not on mobile and it's not already in the items array
  if (
    !isMobile &&
    cachedIBOInfo?.showTopicsSection &&
    !accountItems.some((item) => item.label === "Topics")
  ) {
    accountItems.push({ label: "Topics", icon: "icon-for-topics" });
  }

  const renderAccountItems = () => {
    // Increase issuesCount by 1 if there is no consentData
    const adjustedIssuesCount =
      !consentData && !isANZ ? issuesCount + 1 : issuesCount;

    return accountItems.map(
      (item) =>
        (item.label !== "Account Issues" || adjustedIssuesCount > 0) &&
        !(item.label === "Management Team" && !canAccessMT) && (
          <SettingsListItem
            key={item.label}
            icon={item.icon}
            label={
              item.label === "Account Issues" && adjustedIssuesCount > 0
                ? `${t(item.label)} (${adjustedIssuesCount})`
                : t(item.label)
            }
            onClick={() => handleItemClick(item.label)}
          />
        )
    );
  };

  const filteredMobileItems = items.filter((item) => {
    if (item.label === "Notification Settings") return true;
    if (item.label === "App List Settings") return true;
    return false;
  });

  const renderMobileSettings = () => {
    if (!isMobile) {
      return null;
    }
    return (
      <div>
        <SettingsListHeader
          header={t("App Settings")}
          className={styles.appsContainer}
        />
        {filteredMobileItems.map((item) => (
          <SettingsListItem
            key={item.label}
            icon={item.icon}
            label={t(item.label)}
            onClick={() => handleSettingsClick(item.label)}
          />
        ))}
      </div>
    );
  };

  return (
    <div>
      <Modal
        className={styles.modal}
        isOpen={addressWarnIsOpen}
        children={
          <div className={styles.modalContent}>
            <div className={styles.modalCloseButtonContainer}>
              <img src={addressIcon} className={styles.modalAddressIcon} />
              <img
                src={CloseButton}
                onClick={() => setAddressWarnIsOpen(false)}
                className={styles.modalCloseButton}
              />
            </div>
            <div className={styles.modalText}>
              <div className={styles.modalHeader}>Missing Primary Address</div>
              <div className={styles.modalDescription}>
                In order to select a HomeSite, you must first have a Primary
                Address on file. Please navigate to your{" "}
                <span
                  className={styles.link}
                  onClick={() => {
                    history.push("/addresses");
                    setAddressWarnIsOpen(false);
                  }}
                >
                  address settings
                </span>{" "}
                to proceed.
              </div>
            </div>
          </div>
        }
      />

      <SettingsListHeader header={t("My Account")} />
      {renderAccountItems()}
      {renderMobileSettings()}
    </div>
  );
}

SettingsList.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      icon: PropTypes.string,
      label: PropTypes.string,
      onClick: PropTypes.func,
    })
  ),
  issuesCount: PropTypes.number,
};

export default SettingsList;
